import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { BiCalendar, BiLogoWhatsapp, BiX } from 'react-icons/bi'

import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { SquircleAdapter } from '@/components/SquircleAdapter'
import { useModal } from '@/hooks'
import {
  useCakeMakerActions,
  useCakeMakerState,
  useCakeMakerTotal,
} from '@/store/useCakeMaker'

import { Order } from '@/@types'
import { submitOrder } from '@/services/submitOrder'
import { openLink } from '@/utils'

import './styles.scss'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
}

interface Response {
  message: string
  description: string
  status: number
  code?: string
}

export function ConfirmationModal({ isOpen, onClose }: ConfirmationModalProps) {
  const { reset, persistOrder } = useCakeMakerActions()

  const {
    orderDate,
    candies,
    delivery,
    batter,
    shape,
    weight,
    fillings,
    topping,
    additional,
    theme,
  } = useCakeMakerState()
  const price = useCakeMakerTotal()

  const [name, setName] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [response, setResponse] = useState<Response | null>(null)

  const makeSubmitOrderPayload = (): Order => ({
    customer: name,
    customerWhatsapp: whatsapp,
    date: format(orderDate as Date, 'dd/MM/yyyy'),
    price,
    candies,
    delivery,
    cake: {
      additional,
      batter,
      fillings,
      shape,
      topping,
      weight,
      theme,
    },
  })

  const onSubmit = async () => {
    setIsLoading(true)

    try {
      const order = makeSubmitOrderPayload()
      const response = await submitOrder(order)
      setResponse(response)

      if (response.status !== 200) {
        setResponse({
          message: response.message,
          description: response.description,
          status: response.status,
        })
        return
      }

      setResponse({
        message: response.message,
        description: response.description,
        status: 200,
        code: response.code,
      })

      order.code = response.code

      persistOrder(order)
    } catch {
      setResponse({
        message: 'Erro ao processar pedido.',
        description:
          'Tente novamente mais tarde ou encomende pelo canal oficial do WhatsApp.',
        status: 500,
      })
    }

    setIsLoading(false)
  }

  const onCloseHandler = () => {
    if (response) {
      reset()
    }

    setName('')
    setWhatsapp('')
    setResponse(null)
    onClose()
  }

  const { modalRef } = useModal({ isOpen, onClose: onCloseHandler })

  // O envio troca o formulário pela resposta e destrói o elemento focado.
  useEffect(() => {
    if (response) modalRef.current?.focus()
  }, [response, modalRef])

  return (
    <>
      <div
        className="modal-overlay"
        onClick={onCloseHandler}
        data-open={isOpen}
        aria-hidden
      />

      <SquircleAdapter
        ref={modalRef}
        cornerRadius={0}
        topLeftCornerRadius={24}
        topRightCornerRadius={24}
        bottomLeftCornerRadius={0}
        bottomRightCornerRadius={0}
        className="modal"
        data-open={isOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby={
          response
            ? 'confirmation-modal-title'
            : 'confirmation-modal-form-title'
        }
        aria-describedby={response ? undefined : 'confirmation-modal-desc'}
        tabIndex={-1}
      >
        <div className="modal__header">
          <Button
            className="modal__back-button"
            text="Fechar"
            variant="primary"
            leftRender={<BiX aria-hidden />}
            onClick={onCloseHandler}
            aria-label="Fechar e cancelar confirmação"
          />
        </div>

        <div className="modal__content modal__content-confirmation">
          {response ? (
            <div className="modal__content-confirmation__response">
              <div className="heading">
                <h4 id="confirmation-modal-title">{response.message}</h4>
                <p>{response.description}</p>
              </div>

              <div className="modal__content-confirmation__response__actions">
                <Button
                  className="action-button"
                  text="Entendi"
                  variant="primary"
                  onClick={onCloseHandler}
                />

                {response.code && response.status === 200 ? (
                  <Button
                    text="Entrar em contato"
                    variant="primary"
                    rightRender={<BiLogoWhatsapp size={24} />}
                    onClick={() => {
                      openLink(
                        encodeURI(
                          `https://api.whatsapp.com/send?phone=${process.env.NEXT_PUBLIC_OWNER_PHONE_NUMBER}&text=Olá, meu nome é ${name} e gostaria de falar sobre o pedido ${response.code?.replace('#', '')}.`,
                        ),
                      )
                    }}
                  />
                ) : (
                  <Button
                    text="Entrar em contato"
                    variant="primary"
                    rightRender={<BiLogoWhatsapp size={24} />}
                    onClick={() => {
                      openLink(
                        encodeURI(
                          `https://api.whatsapp.com/send?phone=${process.env.NEXT_PUBLIC_OWNER_PHONE_NUMBER}&text=Olá, meu nome é ${name} e gostaria de falar sobre meu pedido.`,
                        ),
                      )
                    }}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="modal__content-confirmation__form">
              <div className="heading">
                <h4 id="confirmation-modal-form-title">
                  Confirme o agendamento
                </h4>
                <p id="confirmation-modal-desc">
                  É necessário um número de Whatsapp válido para entrarmos em
                  contato com os detalhes do pedido.
                </p>
              </div>

              <div
                className="modal__content-confirmation__form__fields"
                role="group"
                aria-labelledby="confirmation-modal-form-title"
              >
                <Input
                  id="confirmation-name"
                  label="Nome completo"
                  placeholder="Nome *"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  aria-required
                />

                <Input
                  id="confirmation-whatsapp"
                  label="WhatsApp"
                  placeholder="Whatsapp *"
                  name="whatsapp"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  mask="phone"
                  autoComplete="tel"
                  aria-required
                />
              </div>

              <div className="modal__content-confirmation__form__actions">
                <Button
                  text="Garantir agendamento"
                  rightRender={<BiCalendar size={24} />}
                  variant="primary"
                  onClick={onSubmit}
                  disabled={
                    !name.trim() || whatsapp.trim().length < 15 || isLoading
                  }
                  loading={isLoading}
                />
              </div>
            </div>
          )}
        </div>
      </SquircleAdapter>
    </>
  )
}
